import useMenuItems from 'core/widgets/side-menu/useMenuItems'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { AnimatePresence, motion } from 'framer-motion'
import cn from 'utils/cn'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'
import usePathSection from 'core/hooks/navigation/usePathSection'
import React, { ReactElement, useMemo } from 'react'
import Tooltip from 'core/components/dialogs/Tooltip'
import Badge from 'core/widgets/side-menu/Badge'
import MenuItemWrapper from 'core/widgets/side-menu/MenuItemWrapper'
import { matchPath } from 'react-router'
import AlertBadge from 'core/widgets/side-menu/AlertBadge'
import { useSettings } from 'core/hooks/settings/useSettings'
import { MotusIconType } from 'core/components/motus-icons/motus-types'
import MotusIcon from 'core/components/motus-icons/MotusIcons'

const MenuItems = ({ open }: { open: boolean }) => {
  const menuItems = useMenuItems({ open })
  const { pathname, push } = useNavigation()
  const { isTeamSpace } = usePathSection()
  const {
    data: {
      featureFlags: { ENABLE_DRIVER_EXPERIENCE }
    }
  } = useSettings()

  const items = useMemo(() => {
    return Object.values(menuItems).map(
      (
        {
          show,
          url,
          label,
          icon,
          content,
          badge,
          alertBadge,
          tooltip,
          subRoutes,
          isExternal,
          motusIcon
        },
        idx
      ) => {
        if (!show) return null

        if (content) {
          const backButtonLocation = (content as ReactElement)?.props?.to

          if (!open && backButtonLocation) {
            return (
              <Tooltip
                key={`content-${idx}-${isTeamSpace}`}
                content="Back"
                position="right-middle"
                dark={!isTeamSpace}
              >
                <div
                  className="text-center"
                  onClick={() => {
                    push(backButtonLocation)
                  }}
                >
                  <Icon
                    name="arrow-left"
                    size={24}
                    className={cn([
                      isTeamSpace ? 'text-white' : 'text-dark-3',
                      'cursor-pointer hover:opacity-80'
                    ])}
                  />
                </div>
              </Tooltip>
            )
          }

          return open ? (
            <span
              key={`content-${idx}-${isTeamSpace}`}
              className={cn(['w-[200px]'])}
            >
              {React.cloneElement(content as ReactElement, {
                className: isTeamSpace
                  ? '!text-white font-medium'
                  : '!text-dark font-medium'
              })}
            </span>
          ) : (
            <span key={`content-${idx}-${isTeamSpace}`}>&nbsp;</span>
          )
        }

        const cleanUrl = url?.includes('?') ? url.split('?')[0] : url
        const subRoutesMatch = !!subRoutes?.find((path) => {
          return matchPath(path, pathname)
        })
        const isActive = pathname === cleanUrl || subRoutesMatch
        const iconColor =
          icon === 'premium'
            ? 'text-warning-4'
            : isActive
              ? 'text-primary'
              : isTeamSpace
                ? 'text-white'
                : 'text-dark'

        const onClick = () => {
          const pageUrlQueries = JSON.parse(
            sessionStorage.getItem('url_queries') || '{}'
          )
          if (!isActive) {
            const newUrl = pageUrlQueries[cleanUrl]
              ? `${url}?${pageUrlQueries[cleanUrl]}`
              : url
            push(newUrl)
          }
        }

        const renderIcon = () => {
          const defaultIcon = icon ? (
            <Icon
              name={
                icon === 'premium'
                  ? icon
                  : isActive
                    ? icon
                    : (`${icon}-outline` as IconType)
              }
              color={iconColor}
              width={22}
              height={22}
              onClick={onClick}
            />
          ) : (
            <></>
          )

          const iconContent =
            ENABLE_DRIVER_EXPERIENCE && motusIcon ? (
              <MotusIcon
                name={
                  isActive
                    ? motusIcon
                    : (`${motusIcon}-outline` as MotusIconType)
                }
                className={iconColor}
                size={22}
                onClick={onClick}
              />
            ) : (
              defaultIcon
            )

          return (
            <Tooltip
              disabled={open || isActive}
              content={tooltip ?? label}
              position="right-middle"
              dark={!isTeamSpace}
            >
              {iconContent}
            </Tooltip>
          )
        }
        return (
          <MenuItemWrapper
            key={`${url}-content-${idx}-${isTeamSpace}`}
            url={url}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn([
                'flex flex-row items-center select-none gap-2 rounded-lg cursor-pointer pl-[6px] py-2 transition-colors ease-in-out relative',
                isActive && 'bg-white',
                open && 'w-[15rem]',
                !open && 'w-[34px]',
                !isActive && 'hover:!opacity-70'
              ])}
              onClick={onClick}
            >
              {renderIcon()}
              {open && (
                <motion.span
                  className={cn([
                    'font-semibold',
                    isActive
                      ? 'text-primary'
                      : isTeamSpace
                        ? 'text-white!'
                        : 'text-dark!'
                  ])}
                >
                  {label}
                </motion.span>
              )}
              <div className="flex items-center gap-2 absolute right-0 mr-2">
                {badge && open && <Badge>{badge}</Badge>}
                {isExternal && open && (
                  <Icon name="open" color="text-[#707372]" />
                )}
                {alertBadge && open && <AlertBadge>{alertBadge}</AlertBadge>}
              </div>
            </motion.div>
          </MenuItemWrapper>
        )
      }
    )
  }, [ENABLE_DRIVER_EXPERIENCE, isTeamSpace, menuItems, open, pathname, push])

  if (!menuItems || !Object.values(menuItems)?.length) return null

  return (
    <AnimatePresence mode="popLayout">
      <div className="flex flex-col gap-3 mt-5">{items}</div>
    </AnimatePresence>
  )
}

export default MenuItems
